import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { activeClientSession } from './client-session';
import { ACCESS_READ_LIMIT_MS, VerdictBudget } from './route-wait';
import { ClientStore } from '../features/client/domain/+state/client.store';
import { ClientSelectionStore } from '../features/client/domain/+state/client-selection.store';
import { withTimeout } from '../features/projects/domain/utils/with-timeout.util';

/** Одна посадка на загрузку документа: встречный редирект из редактора не должен зациклиться. */
let landingResolved = false;

/**
 * Посадка (или приход из редактора) уже открыла портал — дальше вкладки его. Стор узнаёт о
 * подтверждении позже гарда (сервер помнит выбор только через собранный по нему альбом), поэтому без
 * этой отметки посадка пересуживала бы каждый переход и возвращала ученика с локальной фиксацией в тот
 * же раздел, из которого он вышел. Отметка именная: «Сменить ученика» отдаёт ту же вкладку другому
 * ученику, и портал прошлого не должен проносить его мимо его собственной посадки.
 */
let portalOpen = false;
let portalOpenFor: string | null = null;

/**
 * Дом ученика решает не гард, а `resolveLanding` — и один раз, на первой навигации документа
 * (внешняя ссылка, F5, goto): адрес, который открыл ученик, роли не играет. Дальше гард перестаёт
 * пересуживать, как только дом открыт: переходы по вкладкам («Фотографии», «Альбом») он не трогает,
 * иначе они отбрасывали бы обратно. Гард маршрутизирует, а не защищает: посадка, не успевшая за бюджет,
 * пускает на набранный адрес и уводит позже, только если ученик ещё там.
 */
export const clientAlbumHomeGuard: CanActivateFn = async (route, state): Promise<boolean | UrlTree> => {
  const router = inject(Router);
  const clientStore = inject(ClientStore);
  const selection = inject(ClientSelectionStore);
  const budget = inject(VerdictBudget);

  const session = activeClientSession();
  const linkId = route.paramMap.get('linkId');
  if (!session || !linkId || session.linkId !== linkId) return true;

  // Первая навигация документа (внешняя ссылка, F5) сажает по состоянию один раз. Дальше
  // перехватываем ТОЛЬКО пока дом не открыт: открытый портал (или собственный отбор ученика) ходит
  // по вкладкам свободно.
  const firstLanding = !router.navigated && !landingResolved;
  // Ученик пришёл из редактора — значит альбом у него есть, а альбом ему дом: уводить его оттуда
  // некуда. На этом шаге router.url — ещё ПРЕДЫДУЩИЙ адрес, поэтому приход из редактора виден
  // здесь. Отметку ставим навсегда: следующий хоп («Фотографии» → «Обзор») придёт уже из /client,
  // и без неё посадка снова вернула бы ученика из портала в его дом.
  if (!firstLanding && router.url.startsWith('/editor/')) {
    portalOpen = true;
    portalOpenFor = session.personId;
  }

  const portalMine = portalOpen && portalOpenFor === session.personId;
  const selectionMine = selection.personId() === session.personId && selection.selectionOver();
  if (!firstLanding && (portalMine || selectionMine)) return true;
  landingResolved = true;

  const resolveTarget = async (): Promise<unknown[] | null> => {
    await clientStore.openLink(linkId);
    const projectId = clientStore.projectId();
    if (!projectId) return null;
    // Посадка — единственный судья: адрес, который открыл ученик, роли не играет.
    return selection.resolveLanding(linkId, projectId, session.personId);
  };

  // Портал открывается вместе с применённой посадкой: опоздавшая и брошенная его не трогает.
  const land = (target: unknown[]): true | UrlTree => {
    const section = target[2];
    portalOpen = target[0] === '/client' && (section === 'gallery' || section === 'album');
    portalOpenFor = session.personId;
    const tree = router.createUrlTree(target);
    return router.serializeUrl(tree) === state.url ? true : tree;
  };

  // Angular стартует оба гарда маршрута в одном тике. Часы 1,5 с заводит только посадка (within — до первого
  // ожидания сети). Поздние действия обоих гардов делят один слот бюджета, и посадка, совпавшая с адресом, тоже
  // его занимает. Поздний ПИН-вердикт она не перехватит, пока оба гарда ждут ссылку под одним пределом: ПИН
  // опаздывает, только когда ссылка грузится дольше предела, а тогда опоздавшей посадки уже нет.
  const landing = withTimeout(resolveTarget(), ACCESS_READ_LIMIT_MS).catch(() => null);
  const settled = await budget.within(landing);
  if (settled) return settled.value ? land(settled.value) : true;

  budget.applyLate(
    landing.then(target =>
      target
        ? () => {
            const verdict = land(target);
            if (verdict !== true) budget.redirect(verdict);
          }
        : null,
    ),
    // Поздняя посадка — посадка того, кто открыл адрес: после «Выйти» и входа другого ученика на этом же
    // адресе она чужая и ни уводить, ни открывать портал не смеет.
    url => {
      const now = activeClientSession();
      return url === state.url && now?.personId === session.personId && now.linkId === linkId;
    },
  );
  return true;
};

/**
 * Альбом выключен фотографом — ни раздела «Альбом», ни сборки у ученика нет, на любом заходе (прямой адрес,
 * переход по вкладкам): дом ему — плашка страницы отбора. Ссылку ждём тем же openLink, что и соседние гарды
 * маршрута, — отдельного чтения нет; не дождались — пускаем, судит посадка.
 */
export const clientAlbumOffGuard: CanActivateFn = async route => {
  const router = inject(Router);
  const clientStore = inject(ClientStore);

  const session = activeClientSession();
  const linkId = route.paramMap.get('linkId');
  if (!session || !linkId || session.linkId !== linkId) return true;

  await withTimeout(clientStore.openLink(linkId), ACCESS_READ_LIMIT_MS).catch(() => undefined);
  const person = clientStore.persons().find(item => item.id === session.personId);
  return person?.withBook === false ? router.createUrlTree(['/client', linkId, 'select']) : true;
};
